import { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, X, Crown, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Real PayPal Client ID from backend
const PAYPAL_CLIENT_ID = "Aaf3tYCM5a3VaADd3g2ejLC6APUEF1JxuQmCAHZlL0i_fCyZkm7AurXbYb6Ani2gSTvSYxLkASaFbxnA";

export default function PaymentDialog({ open, onClose, onSuccess, paymentType = "one_time" }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paypalUrl, setPaypalUrl] = useState(null);

  const isOneTime = paymentType === "one_time";
  const amount = isOneTime ? 1.00 : 9.88;
  const currency = "GBP";

  const createOrder = async (data, actions) => {
    console.log('🔵 createOrder called');
    setLoading(true);
    setError(null);
    
    try {
      console.log('📤 Calling backend create-order...');
      // Create order in our backend
      const response = await axios.post(`${API}/payments/create-order`, {
        payment_type: paymentType,
        amount: amount
      }, { withCredentials: true });

      console.log('✅ Order created:', response.data);
      console.log('📋 OrderID:', response.data.orderID);
      
      // Save PayPal URL as fallback
      const orderID = response.data.orderID;
      const fallbackUrl = `https://www.sandbox.paypal.com/checkoutnow?token=${orderID}`;
      setPaypalUrl(fallbackUrl);
      console.log('🔗 PayPal URL:', fallbackUrl);
      
      // Return PayPal order ID
      return orderID;
    } catch (error) {
      console.error('❌ Error creating order:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create payment order';
      setError(errorMsg);
      toast.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onApprove = async (data, actions) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Payment approved, capturing:', data.orderID);
      
      // Capture payment in our backend
      const response = await axios.post(
        `${API}/payments/capture?order_id=${data.orderID}`,
        {},
        { withCredentials: true }
      );

      console.log('Payment captured:', response.data);
      
      toast.success(
        isOneTime 
          ? '🎉 Welcome to Chronos Premium!' 
          : '⚡ Creator subscription activated!',
        { duration: 5000 }
      );
      
      // Wait a bit for user to see success message
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error capturing payment:', error);
      const errorMsg = error.response?.data?.detail || 'Payment capture failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const onError = (err) => {
    console.error('PayPal error:', err);
    setError('An error occurred with PayPal. Please try again.');
    toast.error('Payment error occurred');
  };

  const onCancel = (data) => {
    console.log('Payment cancelled by user');
    toast.info('Payment cancelled');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            {isOneTime ? (
              <>
                <Crown className="h-6 w-6 text-yellow-500" />
                Join Chronos Premium
              </>
            ) : (
              <>
                <Zap className="h-6 w-6 text-purple-500" />
                Become a Creator
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            <div className="text-sm text-muted-foreground">
              {isOneTime ? (
                <div className="space-y-3 mt-4">
                <div className="bg-teal-50 border-2 border-teal-500 rounded-lg p-4">
                  <p className="text-2xl font-bold text-teal-600">£{amount.toFixed(2)} One-Time Payment</p>
                  <p className="text-sm text-gray-600 mt-1">No tricks, no hidden fees. Use forever.</p>
                </div>
                <div className="space-y-2 text-left">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">Unlimited time exchanges</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">Real-time messaging & notifications</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">Automatic translation (140+ languages)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">Premium badge & priority support</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm font-semibold">Lifetime access - pay once, use forever</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3 mt-4">
                <div className="bg-purple-50 border-2 border-purple-500 rounded-lg p-4">
                  <p className="text-2xl font-bold text-purple-600">£{amount.toFixed(2)}/month</p>
                  <p className="text-sm text-gray-600 mt-1">For teachers & course creators</p>
                </div>
                <div className="space-y-2 text-left">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">All Premium features</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">Create & host courses</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">Group sessions & workshops</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">Creator analytics dashboard</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span className="text-sm">Special Creator badge</span>
                  </div>
                </div>
              </div>
            )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {paypalUrl && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700 mb-2">
                💡 If PayPal window doesn't open automatically:
              </p>
              <a
                href={paypalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                Open PayPal Payment Page →
              </a>
            </div>
          )}
          
          <PayPalScriptProvider
            options={{
              clientId: PAYPAL_CLIENT_ID,
              currency: currency,
              intent: "capture"
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Processing payment...</p>
                </div>
              </div>
            ) : (
              <PayPalButtons
                style={{ 
                  layout: "vertical",
                  color: "gold",
                  shape: "rect",
                  label: "paypal"
                }}
                createOrder={createOrder}
                onApprove={onApprove}
                onError={onError}
                onCancel={onCancel}
                forceReRender={[amount, currency]}
              />
            )}
          </PayPalScriptProvider>
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            🔒 Secure payment powered by PayPal
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Sandbox mode - Use test PayPal account
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}